import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, apiClient, useLanguage } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import PageHeader from "../components/layout/PageHeader";
import { getDoaById, DOA_CATEGORIES, getDoasByCategory, DUAS } from "../services/doaService";
import { toast } from "sonner";
import { updateDzikirProgress, showChallengeProgressToast } from "../utils/challengeHelper";
import {
  Copy,
  Share2,
  RotateCcw,
  Heart,
  Volume2,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

const DoaDetail = () => {
  const { doaId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const [doa, setDoa] = useState(null);
  const [count, setCount] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  // Helper function to get local date in YYYY-MM-DD format
  const getLocalDate = (date = new Date()) => {
    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get category from URL params
  const categoryFromUrl = searchParams.get('category') || 'all';

  // Find previous and next doa in the same category
  const getAdjacentDoas = () => {
    if (!doa) return { previous: null, next: null };

    const categoryDuas = getDoasByCategory(categoryFromUrl);
    const currentIndex = categoryDuas.findIndex(d => d.id === doaId);

    return {
      previous: currentIndex > 0 ? categoryDuas[currentIndex - 1] : null,
      next: currentIndex < categoryDuas.length - 1 ? categoryDuas[currentIndex + 1] : null,
    };
  };

  const { previous, next } = getAdjacentDoas();

  // Navigate to adjacent doa
  const navigateToDoa = (newDoaId) => {
    const categoryParam = categoryFromUrl === 'all' ? '' : `?category=${categoryFromUrl}`;
    navigate(`/doa/${newDoaId}${categoryParam}`);
  };

  useEffect(() => {
    const doaData = getDoaById(doaId);
    if (doaData) {
      setDoa(doaData);
      loadTodayCount(doaId);
    } else {
      navigate("/doa");
    }
    // Scroll to top when doa changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [doaId]);

  const loadTodayCount = async (id) => {
    try {
      const today = getLocalDate();
      const res = await apiClient.get(`/dzikir/logs/${today}`);
      const log = res.data.find(l => l.dzikir_id === id);
      if (log) {
        setCount(log.count);
      }
    } catch (error) {
      console.error("Error loading count:", error);
    }
  };

  const incrementCount = async () => {
    const newCount = count + 1;
    setCount(newCount);

    try {
      await apiClient.post(`/dzikir/log?dzikir_id=${doa.id}&count=${newCount}`);

      // Auto-update dzikir challenge progress
      const challengeResult = await updateDzikirProgress(1);

      // Show toast notification if challenge progress updated
      if (challengeResult.success) {
        const toastData = showChallengeProgressToast(challengeResult, isEnglish);
        if (toastData) {
          toast[toastData.type](toastData.title, { description: toastData.description });
        }
      }
    } catch (error) {
      console.error("Error logging dzikir:", error);
    }

    // Vibrate if supported
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  const resetCount = () => {
    setCount(0);
    toast.success(isEnglish ? "Counter reset" : "Counter direset");
  };

  const copyText = () => {
    const text = `${doa.arabic}\n\n${doa.transliteration}\n\n${isEnglish ? doa.english : doa.indonesian}`;
    navigator.clipboard.writeText(text);
    toast.success(isEnglish ? "Copied to clipboard" : "Disalin ke clipboard");
  };

  const shareText = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: isEnglish ? doa.titleEn : doa.title,
          text: `${doa.arabic}\n\n${isEnglish ? doa.english : doa.indonesian}`,
        });
      } catch (error) {
        copyText();
      }
    } else {
      copyText();
    }
  };

  const category = DOA_CATEGORIES.find(c => c.id === doa?.category);
  const targetReached = doa?.repeat && count >= doa.repeat;

  if (!doa) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="doa-detail-page">
      <PageHeader
        title={isEnglish ? doa.titleEn : doa.title}
        showBack
        onBackClick={() => {
          // Navigate back to Doa page with category parameter
          const categoryParam = categoryFromUrl === 'all' ? '' : `?category=${categoryFromUrl}`;
          navigate(`/doa${categoryParam}`);
        }}
        rightContent={
          <Button
            variant="ghost"
            size="icon"
            className={`h-9 w-9 sm:h-10 sm:w-10 ${isFavorite ? "text-red-500" : ""}`}
            onClick={() => setIsFavorite(!isFavorite)}
          >
            <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${isFavorite ? "fill-current" : ""}`} />
          </Button>
        }
      />

      <div className="page-container space-y-4 sm:space-y-5">
        {/* Category Badge */}
        {category && (
          <Badge variant="secondary" className="w-fit text-xs">
            {isEnglish ? category.englishName : category.name}
          </Badge>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-2 sm:gap-3">
          <Button
            variant="outline"
            size="sm"
            className={`flex-1 text-xs sm:text-sm ${!previous ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!previous}
            onClick={() => previous && navigateToDoa(previous.id)}
          >
            <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">{isEnglish ? 'Previous' : 'Sebelumnya'}</span>
            <span className="xs:hidden">{isEnglish ? 'Prev' : 'Prev'}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={`flex-1 text-xs sm:text-sm ${!next ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!next}
            onClick={() => next && navigateToDoa(next.id)}
          >
            {isEnglish ? 'Next' : 'Selanjutnya'}
            <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1 sm:ml-2" />
          </Button>
        </div>

        {/* Show current position in category */}
        {(previous || next) && (
          <p className="text-xs text-muted-foreground text-center">
            {isEnglish ? 'Navigate through this category' : 'Navigasi kategori ini'}
          </p>
        )}

        {/* Arabic Text */}
        <Card>
          <CardContent className="p-4 sm:p-5 md:p-6">
            <p className="font-arabic text-xl sm:text-2xl md:text-3xl text-right leading-loose sm:leading-[2.5]" dir="rtl">
              {doa.arabic}
            </p>
          </CardContent>
        </Card>

        {/* Transliteration */}
        <Card>
          <CardContent className="p-3 sm:p-4 md:p-5">
            <h3 className="text-xs font-medium text-muted-foreground mb-1.5 sm:mb-2">{isEnglish ? "Transliteration" : "Transliterasi"}</h3>
            <p className="italic text-sm sm:text-base text-foreground">{doa.transliteration}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 sm:p-4 md:p-5">
            <h3 className="text-xs font-medium text-muted-foreground mb-1.5 sm:mb-2">{isEnglish ? "Meaning (English)" : "Arti (Indonesia)"}</h3>
            <p className="text-sm sm:text-base text-foreground leading-relaxed">{isEnglish ? doa.english : doa.indonesian}</p>
          </CardContent>
        </Card>

        {/* Reference */}
        {doa.reference && (
          <p className="text-xs text-muted-foreground text-center">
            {isEnglish ? `Reference: ${doa.reference}` : `Referensi: ${doa.reference}`}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-2 sm:gap-3">
          <Button variant="outline" size="sm" className="flex-1 text-xs sm:text-sm" onClick={copyText}>
            <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> {isEnglish ? "Copy" : "Salin"}
          </Button>
          <Button variant="outline" size="sm" className="flex-1 text-xs sm:text-sm" onClick={shareText}>
            <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> {isEnglish ? "Share" : "Bagikan"}
          </Button>
        </div>

        {/* Dzikir Counter */}
        {doa.repeat > 1 && (
          <Card className={targetReached ? "border-primary border-2" : ""}>
            <CardContent className="p-4 sm:p-5 md:p-6 text-center">
              <h3 className="font-semibold text-sm sm:text-base mb-3 sm:mb-4">
                {isEnglish ? `Dhikr Counter (${doa.repeat}x)` : `Dzikir Counter (${doa.repeat}x)`}
              </h3>

              <button
                className={`dzikir-counter-btn mx-auto w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 text-2xl sm:text-3xl md:text-4xl ${targetReached ? "bg-green-500" : ""}`}
                onClick={incrementCount}
                data-testid="dzikir-counter-btn"
              >
                {count}
              </button>

              <p className="text-xs sm:text-sm text-muted-foreground mt-3 sm:mt-4">
                {targetReached
                  ? (isEnglish ? "Target reached! Alhamdulillah" : "Target tercapai! Alhamdulillah")
                  : (isEnglish ? `${doa.repeat - count} more to reach the target` : `${doa.repeat - count} lagi untuk mencapai target`)}
              </p>

              <Button
                variant="ghost"
                size="sm"
                className="mt-2 sm:mt-3 text-xs sm:text-sm h-8 sm:h-9 px-3 sm:px-4"
                onClick={resetCount}
              >
                <RotateCcw className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> {isEnglish ? "Reset" : "Reset"}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DoaDetail;
